import React, { useEffect, useState } from 'react';
import { ModuleTitle } from './utilities/ModuleTitle.tsx'
import { GeneInput } from './utilities/GeneInput.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faQuestion } from '@fortawesome/free-solid-svg-icons';
import { makeMorphNameString } from '../utils/GeneCalculator.ts';
import { MorphSelect } from './utilities/MorphSelect.tsx';
import { ResultsModal, Parent } from './utilities/ResultsModal.tsx';
import { driver } from 'driver.js';
import { Gene, blankGene } from '../types/Gene.ts';
import { Morph } from '../types/Morph.ts';
import { useRepToolContext } from '../RepToolContext.tsx';


const CalcButtons = ({ clearInput, addGene, toggleTutorial }: {
  clearInput: Function;
  addGene: Function;
  toggleTutorial: () => void;
}) => (
  <div className='d-flex flex-row ml-auto'>
    <b className='mr-4' onClick={() => toggleTutorial()}>
      <FontAwesomeIcon className='mr-2' icon={faQuestion} />
    </b>
    <b id="clear-button" className='mr-4' onClick={() => clearInput()}>
      Clear
    </b>
    <div id="add-button" className='d-flex flex-row' onClick={() => addGene()}>
      <b>Add row</b>
      <FontAwesomeIcon className='ml-2 my-auto' icon={faPlus} />
    </div>
  </div>
)


export const GeneticCalculator = ({ backToMenu, genes, morphs }: { backToMenu: () => void, genes: Gene[], morphs: Morph[] }) => {
  const nonLineGenes = genes.filter(gene => gene.gene_type && gene.gene_type !== "(Line Bred)" && gene.gene_type !== "(Unproven)");
  const nonLineMorphs = morphs.filter(morph => morph.genes.every(gene => gene.gene_type && gene.gene_type !== "(Line Bred)" && gene.gene_type !== "(Unproven)"));
  const [maleGenes, setMaleGenes] = useState<Gene[]>([blankGene]);
  const [femaleGenes, setFemaleGenes] = useState<Gene[]>([blankGene]);
  const [showResults, setShowResults] = useState(false);
  const [maleMorph, setMaleMorph] = useState<Morph | null>(null);
  const [femaleMorph, setFemaleMorph] = useState<Morph | null>(null);
  const [parents, setParents] = useState<Parent[]>([]);
  const {appendControls} = useRepToolContext();


  const driverObj = driver({
    steps: [
      { popover: { title: 'Genetic Calculator', description: 'Use this tool to calculate the possible outcomes of breeding two reptiles together. The calculator will then show you the possible outcomes of the pairing.' } },
      { element: '#morph_selects', popover: { title: 'Fill in by morph name', description: 'You can fill in the inputs for each side by typing in the name of the morph, RepTool will fill in the genes for you. (Optional)' } },
      { element: '#gene_selects', popover: { title: 'Fill in by gene name', description: 'Alternatively, you can also fill in each gene manually if the morph you are looking for isn\'t listed.' } },
      { element: '#male_container', popover: { title: 'Hom/Het Switch', description: 'If the gene is recessive, you can switch between homozygous and heterozygous. Or if the gene is codominant or dominant you can use this to specify if the gene is \'super\' (homozygous).' } },
      { element: '#add-button', popover: { title: 'Add Row', description: 'If you need more rows click this button to create another row of inputs.' } },
      { element: '#clear-button', popover: { title: 'Clear Inputs', description: 'If you want to clear all the inputs, click this button.' } },
      { element: '#calculator-btn', popover: { title: 'Calculate Pairings', description: 'Once you have filled in the inputs, click this button to calculate the possible outcomes of the pairing.' } },
    ]
  });





  const morphSelect = (genes) => {
    return genes.map(gene => {
      return {
        gene_name: gene.gene_name,
        gene_type: gene.gene_type,
        isHet: gene.is_super,
        expression: gene.expression || 'NORMAL'
      }
    })
  }

  const addGene = () => {
    const max = Math.max(maleGenes.length, femaleGenes.length);
    const bothEqual = maleGenes.length === femaleGenes.length;
    if (bothEqual || max > maleGenes.length) {
      setMaleGenes([...maleGenes, blankGene]);
    }
    if (bothEqual || max > femaleGenes.length) {
      setFemaleGenes([...femaleGenes, blankGene]);
    }
  }
  useEffect(() => {
    const father = {
      genes: maleGenes,
      morph_name: makeMorphNameString(maleGenes, morphs).finalResults,
    }
    const mother = {
      genes: femaleGenes,
      morph_name: makeMorphNameString(femaleGenes, morphs).finalResults,
    }

    let newParents = [father, mother]

    setParents(newParents)

  }, [maleGenes, femaleGenes])

  const resetInputs = () => {
    setFemaleGenes([blankGene]);
    setMaleGenes([blankGene]);
    setMaleMorph(null);
    setFemaleMorph(null);
  }
  useEffect(() => {
    if (!showResults) {
      appendControls(<CalcButtons addGene={addGene} clearInput={resetInputs} toggleTutorial={() => driverObj.drive()} />, "Genetic Calculator");
    } else {
      appendControls(null, "Results Genetic Calculator");
    }
  }, [showResults, maleGenes, femaleGenes])
  return (<div className='fadeIn animated'>

    <ModuleTitle title="Genetic Calculator" backToMenu={() => backToMenu()} />
    <div className="container mt-0" >
      <div id="calculator_container"
      >
        <div className="d-flex text-center mb-2 flex-row">
          <h6 className="w-50">Male</h6>
          <h6 className="w-50">Female</h6>
        </div>
        <div id="morph_selects" className="d-flex mt-3 mb-2 border-bottom flex-row">
          <div id="male_morph_container" className="w-50 px-2">
            <MorphSelect
              id="male-morph"
              morphs={nonLineMorphs}
              morphTyped={(morph) => {
                setMaleGenes(morphSelect(morph.genes));
                setMaleMorph(morph)
              }}
              morph={maleMorph}
            />
          </div>
          <div id="female_morph_container" className="w-50 px-2">
            <MorphSelect id="female-morph"
              morphs={nonLineMorphs}
              morphTyped={(morph) => {
                setFemaleGenes(morphSelect(morph.genes));
                setFemaleMorph(morph);
              }}
              morph={femaleMorph} />
          </div>
        </div>
        <div id='gene_selects' className="d-flex flex-row">
          <div id="male_container" className="w-50 px-2">
            {
              maleGenes.map((gene, index) => (
                <div
                  className="mt-3"
                  key={`male-${index}`}
                >
                  <GeneInput
                    id={`male-${index}`}
                    genes={nonLineGenes}
                    gene={gene}
                    setGene={(geneData: Gene) => {
                      const updateGenes = [...maleGenes];
                      updateGenes[index] = geneData;
                      setMaleGenes(updateGenes)
                    }}
                  />
                </div>
              ))
            }
          </div>
          <div id="female_container" className="w-50 px-2">
            {
              femaleGenes.map((gene, index) => (
                <div
                  className='mt-3'
                  key={`female-${index}`}
                >
                  <GeneInput
                    id={`female-${index}`}
                    genes={nonLineGenes}
                    gene={gene}
                    setGene={(geneData: Gene) => {
                      const updateGenes = [...femaleGenes];
                      updateGenes[index] = geneData;
                      setFemaleGenes(updateGenes);
                    }}
                  />
                </div>
              ))
            }
          </div>
        </div>
        <div className="col-md-6 mx-auto col-sm-8 mt-5 text-center">
          <div id='calculator-btn' className="calculate btn btn-outline-light w-100 button" onClick={() => {

            appendControls(null, "Results Genetic Calculator");
            setShowResults(true);
          }}>Calculate Pairings</div>
        </div>
        {showResults &&
          <ResultsModal
            closeModal={() => setShowResults(false)}
            parents={parents}
            morphs={morphs}
            genes={genes}
          />}
      </div>
    </div>
  </div>
  );
}

